import posthog, { PostHog } from 'posthog-js';
import { flushErrorQueue } from '../utils/errorTracking';

// Detect environment
const getEnvironment = () => {
  const mode = import.meta.env.MODE || 'development';
  const isDev = import.meta.env.DEV;
  const isProd = import.meta.env.PROD;
  
  // Determine environment based on mode and URL
  if (mode === 'localhost' || window.location.hostname === 'localhost') {
    return 'localhost';
  } else if (mode === 'development' || window.location.hostname.includes('dev')) {
    return 'development';
  } else if (mode === 'production' || isProd) {
    return 'production';
  }
  
  return 'development'; // fallback
};

export const environment = getEnvironment();
export const isProduction = environment === 'production';
export const isDevelopment = environment === 'development';
export const isLocalhost = environment === 'localhost';

export const posthogConfig = {
  apiKey: import.meta.env.VITE_POSTHOG_API_KEY || '',
  apiHost: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
  environment,
  personProfiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
  capturePageView: false, // We'll handle this manually with React Router
  capturePageLeave: true,
  disableSessionRecording: false,
  enableRecordingConsoleLogs: !isProduction, // Only in dev/localhost
  enableExceptionCapture: true, // Enable error tracking in all environments
  autocapture: {
    dom_event_handlers: true,
    url_allowlist: ['.*'], // Allow all URLs
  },
  // Session recording is more aggressive in development
  sessionRecordingOptions: {
    maskAllInputs: isProduction, // Mask inputs in production for privacy
    maskTextContent: isProduction, // Mask text in production
    blockClass: 'ph-no-capture',
    ignoreClass: 'ph-ignore',
  },
  loaded: (posthogInstance: PostHog) => {
    const config = {
      environment,
      apiKey: posthogConfig.apiKey ? 'Set' : 'Not set',
      apiHost: posthogConfig.apiHost,
      enableExceptionCapture: posthogConfig.enableExceptionCapture,
      mode: import.meta.env.MODE,
      isDev: import.meta.env.DEV,
      isProd: import.meta.env.PROD,
    };
    
    if (!isProduction) {
      console.log('PostHog loaded successfully');
      console.log('PostHog config:', config);
    }
    
    // Flush any queued errors
    flushErrorQueue();
  },
};

export const initializePostHog = () => {
  if (!isProduction) {
    console.log('Initializing PostHog...', {
      environment,
      apiKey: posthogConfig.apiKey ? 'Set' : 'Not set',
      apiHost: posthogConfig.apiHost ? 'Set' : 'Not set',
      enableExceptionCapture: posthogConfig.enableExceptionCapture,
      mode: import.meta.env.MODE,
    });
  }
  
  if (posthogConfig.apiKey && typeof window !== 'undefined') {
    try {
      posthog.init(posthogConfig.apiKey, {
        api_host: posthogConfig.apiHost,
        person_profiles: posthogConfig.personProfiles,
        capture_pageview: posthogConfig.capturePageView,
        capture_pageleave: posthogConfig.capturePageLeave,
        disable_session_recording: posthogConfig.disableSessionRecording,
        enable_recording_console_logs: posthogConfig.enableRecordingConsoleLogs,
        enable_exception_capture: posthogConfig.enableExceptionCapture,
        autocapture: posthogConfig.autocapture,
        session_recording: posthogConfig.sessionRecordingOptions,
        loaded: posthogConfig.loaded,
      });
      
      // Set environment as a super property (will be included in all events)
      posthog.register({
        environment,
        app_version: import.meta.env.VITE_APP_VERSION || 'unknown',
        build_mode: import.meta.env.MODE,
      });
      
      if (!isProduction) {
        console.log('PostHog initialization called');
        
        // Wait a bit and check if PostHog is loaded
        setTimeout(() => {
          console.log('PostHog status after init:', {
            loaded: posthog.__loaded,
            environment,
            superProperties: posthog.get_property('environment'),
          });
        }, 1000);
      }
    } catch (error) {
      console.error('PostHog initialization error:', error);
      
      // Track initialization error
      if (posthog.__loaded) {
        posthog.capture('posthog_init_error', {
          error: error instanceof Error ? error.message : String(error),
          environment,
        });
      }
    }
  } else {
    if (!isProduction) {
      console.warn('PostHog not initialized:', {
        hasApiKey: !!posthogConfig.apiKey,
        isWindow: typeof window !== 'undefined',
        environment,
      });
    }
  }
};

export default posthog;
